/**
 * Typed fetch wrapper for the Worker API. Cloudflare Access injects its
 * own session cookie automatically for same-site requests — this client
 * does not (and must not) manage any auth token itself; `credentials:
 * "include"` just ensures that cookie is sent.
 */

import type { ApiError } from "../types/api";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export class ApiRequestError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export async function apiGet<T>(path: string, params?: Record<string, string | number>): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, String(value));
    }
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    let message = `Request failed (${response.status}).`;
    try {
      const body = (await response.json()) as ApiError;
      if (body?.error) message = body.error;
    } catch {
      // response body wasn't JSON — keep the generic message
    }
    throw new ApiRequestError(message, response.status);
  }

  return (await response.json()) as T;
}
